"use client";

import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { MessageSquare, Plus, Search, ChevronDown, ChevronRight, Lock, Pencil, Trash2, MoreHorizontal, ArrowUp, ArrowDown, Megaphone, X, Users, Check, Trophy, Building2, Wifi, WifiOff, ChevronUp } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/app/_module/components/ui/dialog";
import { Button } from "@/app/_module/components/ui/button";
import { ScrollArea } from "@/app/_module/components/ui/scroll-area";
import { Separator } from "@/app/_module/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/app/_module/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/app/_module/components/ui/dropdown-menu";
import { getInitials, statusColor, statusLabel } from "./helpers";
import type { Channel, ChannelMember } from "./TeamChatService";

export interface ChannelSidebarProps {
  channels: Channel[];
  activeChannelId: string | null;
  onSelect: (id: string) => void;
  onCreateChannel: (name: string) => void;
  onEditChannel?: (channelId: string, newName: string) => void;
  onDeleteChannel?: (channelId: string) => void;
  onReorderChannel?: (channelId: string, direction: 'up' | 'down') => void;
  onToggleAnnouncement?: (channelId: string) => void;
  onStartDm: (userId: string, userName: string) => void;
  onCreateGroupDm?: (userIds: string[], groupName?: string) => void;
  agencyName?: string;
  agencyLogo?: string;
  allMembers: ChannelMember[];
  currentUserId?: string;
  currentUserAgencyRole?: string;
  currentAgencyId?: string;
  agencyList?: Array<{ id: string; name: string; isCurrent: boolean; role?: string; parentAgencyId?: string; memberCount?: number; onlineCount?: number; logoUrl?: string }>;
  chatAgencyId?: string | null;
  onSwitchAgency?: (agencyId: string | null) => void;
  searchInputRef?: React.RefObject<HTMLInputElement | null>;
  networkMembers?: Array<{ agencyId: string; agencyName: string; parentAgencyId: string | null; depth: number; members: Array<{ userId: string; name: string; email: string; profileImageUrl?: string; isOnline?: boolean; userStatus?: string; agencyRole?: string }> }>;
  isOwnerOrAdmin?: boolean;
}

export function ChannelSidebar({
  channels,
  activeChannelId,
  onSelect,
  onCreateChannel,
  onEditChannel,
  onDeleteChannel,
  onReorderChannel,
  onToggleAnnouncement,
  onStartDm,
  onCreateGroupDm,
  agencyName,
  agencyLogo,
  allMembers,
  currentUserId,
  currentUserAgencyRole,
  currentAgencyId,
  agencyList,
  chatAgencyId,
  onSwitchAgency,
  searchInputRef,
  networkMembers,
  isOwnerOrAdmin,
}: ChannelSidebarProps) {
  const [search, setSearch] = useState("");
  const [channelsOpen, setChannelsOpen] = useState(true);
  const [dmsOpen, setDmsOpen] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [editingChannelId, setEditingChannelId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [hiddenDms, setHiddenDms] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem('team-chat-hidden-dms');
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch { return new Set(); }
  });
  const [isGroupDmOpen, setIsGroupDmOpen] = useState(false);
  const [groupDmSelected, setGroupDmSelected] = useState<Set<string>>(new Set());
  const [groupDmSearch, setGroupDmSearch] = useState("");
  const [groupDmName, setGroupDmName] = useState("");

  const filteredGroupMembers = useMemo(() => {
    return allMembers
      .filter(m => m.userId !== currentUserId)
      .filter(m => {
        if (!groupDmSearch) return true;
        const q = groupDmSearch.toLowerCase();
        return (m.user.name?.toLowerCase().includes(q) || m.user.email?.toLowerCase().includes(q));
      });
  }, [allMembers, currentUserId, groupDmSearch]);

  // Sync hiddenDms from localStorage when channels change (e.g. after startDm unhides)
  useEffect(() => {
    try {
      const stored = localStorage.getItem('team-chat-hidden-dms');
      const fromStorage = stored ? new Set<string>(JSON.parse(stored)) : new Set<string>();
      setHiddenDms(prev => {
        if (prev.size !== fromStorage.size || [...prev].some(id => !fromStorage.has(id))) {
          return fromStorage;
        }
        return prev;
      });
    } catch {}
  }, [channels]);

  const closeDm = (channelId: string) => {
    setHiddenDms(prev => {
      const next = new Set(prev);
      next.add(channelId);
      localStorage.setItem('team-chat-hidden-dms', JSON.stringify([...next]));
      return next;
    });
    // If this was the active channel, deselect
    if (channelId === activeChannelId) {
      const general = channels.find(c => c.channel.isDefault);
      if (general) onSelect(general.channel.id);
    }
  };

  // Auto-unhide DMs that get new messages
  useEffect(() => {
    const toUnhide = channels.filter(c => c.channel.type === 'dm' && c.unreadCount > 0 && hiddenDms.has(c.channel.id));
    if (toUnhide.length > 0) {
      setHiddenDms(prev => {
        const next = new Set(prev);
        toUnhide.forEach(c => next.delete(c.channel.id));
        localStorage.setItem('team-chat-hidden-dms', JSON.stringify([...next]));
        return next;
      });
    }
  }, [channels, hiddenDms]);

  // Announcement channels — shown in their own section
  // Only org-wide announcement channels (slug: org-announcements) go in Agency-Wide section
  // Custom channels toggled as announcement stay in Channels
  const announcementChannels = channels
    .filter(c => c.channel.type === "channel" && c.channel.isAnnouncement && c.channel.slug === "org-announcements")
    .sort((a, b) => {
      const orderA = a.channel.displayOrder ?? (a.channel as any).sortOrder ?? 999;
      const orderB = b.channel.displayOrder ?? (b.channel as any).sortOrder ?? 999;
      if (orderA !== orderB) return orderA - orderB;
      return (a.channel.name || '').localeCompare(b.channel.name || '');
    });

  // Check if the current user can post in an announcement channel
  // Only owner/admin of the channel's owning agency can post
  const canPostInAnnouncement = (ch: Channel) => {
    const channelAgencyId = ch.channel.agencyId;
    const role = currentUserAgencyRole?.toLowerCase() || '';
    // If the channel belongs to the user's current agency and they are owner/admin, they can post
    if (channelAgencyId === currentAgencyId && ['owner', 'admin'].includes(role)) return true;
    // If user has owner/admin role in the channel's agency (check agencyList)
    const agencyEntry = agencyList?.find(a => a.id === channelAgencyId);
    if (agencyEntry && ['owner', 'admin'].includes(agencyEntry.role || '')) return true;
    return false;
  };

  const [announcementsOpen, setAnnouncementsOpen] = useState(true);

  // Show all non-DM channels except org-wide announcements (those go in Agency-Wide section)
  const groupChannels = channels
    .filter(c => c.channel.type === "channel" && !(c.channel.isAnnouncement && c.channel.slug === "org-announcements"))
    .sort((a, b) => {
      // Default channels first, then by sortOrder, then alphabetical
      if (a.channel.isDefault && !b.channel.isDefault) return -1;
      if (!a.channel.isDefault && b.channel.isDefault) return 1;
      const orderA = (a.channel as any).sortOrder ?? 999;
      const orderB = (b.channel as any).sortOrder ?? 999;
      if (orderA !== orderB) return orderA - orderB;
      return (a.channel.name || '').localeCompare(b.channel.name || '');
    });
  const dmChannels = channels
    .filter(c => {
      if (c.channel.type !== "dm") return false;
      if (c.channel.id === activeChannelId) return true;
      if (c.unreadCount > 0) return true;
      return !hiddenDms.has(c.channel.id);
    })
    .sort((a, b) => {
      // Most recently active DM first (like Discord/Slack)
      const timeA = new Date(a.channel.lastMessageAt || (a.channel as any).updatedAt || a.channel.createdAt || 0).getTime();
      const timeB = new Date(b.channel.lastMessageAt || (b.channel as any).updatedAt || b.channel.createdAt || 0).getTime();
      return timeB - timeA;
    });

  // For DMs, show the OTHER person's CURRENT name (not from channel name which can be stale)
  const getDmDisplayName = (ch: Channel) => {
    if (ch.channel.type !== "dm") return ch.channel.name;
    const slug = ch.channel.slug || '';

    // Self-DM check
    if (currentUserId && slug.replace('dm-', '') === currentUserId) {
      return 'Personal Notes';
    }

    // Extract both user IDs from slug: "dm-{uuid1}-{uuid2}" where UUIDs are 36 chars with dashes
    // Strategy: check each member's FULL userId against the slug
    if (currentUserId && slug && allMembers.length > 0) {
      for (const member of allMembers) {
        if (member.userId === currentUserId) continue;
        // Match full UUID in the slug (handles both truncated and full slugs)
        if (slug.includes(member.userId)) {
          return member.user.name || member.user.email;
        }
      }
      // If full UUID didn't match (slug may be truncated), try progressively longer prefixes
      for (const member of allMembers) {
        if (member.userId === currentUserId) continue;
        // Use enough chars to be unique — at least 30 chars of the UUID
        const prefix = member.userId.slice(0, 30);
        if (slug.includes(prefix)) {
          return member.user.name || member.user.email;
        }
      }
    }

    // Fallback: parse from channel name "Name1 & Name2"
    const names = ch.channel.name.split(" & ");
    if (names.length < 2) return names[0] || "Direct Message";
    const myName = allMembers.find(m => m.userId === currentUserId)?.user?.name;
    if (myName) {
      const other = names.find(n => n !== myName);
      if (other) return other;
    }
    return names[1] || names[0] || "Direct Message";
  };
  const filtered = search
    ? channels.filter(c => c.channel.name.toLowerCase().includes(search.toLowerCase()))
    : null;

  const renderItem = (ch: Channel) => {
    const isActive = ch.channel.id === activeChannelId;
    const isDm = ch.channel.type === "dm";
    const isArchived = ch.channel.description?.startsWith('[ARCHIVED]');
    const isDefault = ch.channel.isDefault;
    const isEditing = editingChannelId === ch.channel.id;

    if (isEditing && !isDm) {
      return (
        <div key={ch.channel.id} className="px-2 py-1">
          <div className="flex items-center gap-1 bg-zinc-800 rounded-md px-2 py-1">
            <MessageSquare className="w-3.5 h-3.5 text-zinc-500 flex-shrink-0" />
            <input
              value={editName}
              onChange={e => setEditName(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter" && editName.trim() && onEditChannel) {
                  onEditChannel(ch.channel.id, editName.trim());
                  setEditingChannelId(null);
                  setEditName("");
                }
                if (e.key === "Escape") { setEditingChannelId(null); setEditName(""); }
              }}
              className="flex-1 bg-transparent text-sm text-zinc-300 placeholder:text-zinc-600 border-none outline-none"
              autoFocus
            />
          </div>
          <p className="text-[10px] text-zinc-600 px-1 mt-1">Enter to save, Esc to cancel</p>
        </div>
      );
    }

    return (
      <ChannelItemRow key={ch.channel.id} ch={ch} isActive={isActive} isDm={isDm} isArchived={!!isArchived} isDefault={!!isDefault}
        onSelect={onSelect} onEditChannel={onEditChannel} onDeleteChannel={onDeleteChannel} onReorderChannel={onReorderChannel} onToggleAnnouncement={onToggleAnnouncement}
        getDmDisplayName={getDmDisplayName} setEditingChannelId={setEditingChannelId} setEditName={setEditName} onCloseDm={closeDm} />
    );
  };

  return (
    <div role="navigation" aria-label="Channel navigation" className="flex flex-col h-full w-full min-h-0 overflow-hidden bg-zinc-900 text-zinc-100">
      {/* Header */}
      <div className="px-4 pt-4 pb-3 relative z-50">
        <div className="flex items-center gap-2 mb-1">
          {agencyLogo && (
            <img src={agencyLogo} alt="" className="w-7 h-7 rounded-md object-cover flex-shrink-0" />
          )}
          <div className="min-w-0 flex-1 relative">
            {/* Agency switcher dropdown — only show if there are sub-agencies */}
            {agencyList && agencyList.length > 1 && onSwitchAgency ? (
              <AgencyTreeSwitcher
                agencyList={agencyList}
                chatAgencyId={chatAgencyId}
                agencyName={agencyName}
                onSwitchAgency={onSwitchAgency}
              />
            ) : (
              <h2 className="text-sm font-bold text-white truncate">{agencyName || "Team Chat"}</h2>
            )}
            <p className="text-[10px] text-zinc-500 leading-none">
              {channels.some(c => c.agencyName) ? "Agency-Wide Chat" : "Team Chat"}
            </p>
          </div>
        </div>
        <div className="relative mt-3">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
          <input
            ref={searchInputRef}
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search channels..."
            aria-label="Search channels"
            className="w-full bg-zinc-800 border-none rounded-md pl-8 pr-3 py-1.5 text-sm text-zinc-300 placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-blue-500/50"
          />
        </div>
      </div>

      <Separator className="bg-zinc-800" />

      {/* Channel list */}
      <ScrollArea className="flex-1 px-2 py-2" aria-label="Channel list">
        {filtered ? (
          <div role="list" className="space-y-0.5">
            {filtered.map(renderItem)}
            {filtered.length === 0 && (
              <p className="text-xs text-zinc-500 text-center py-4">No channels found</p>
            )}
          </div>
        ) : (
          <>
            {/* Agency-Wide section — shows announcement channels with premium styling */}
            {announcementChannels.length > 0 && (
              <div className="mb-3">
                <div className="flex items-center gap-1 px-2 py-1 w-full text-xs font-semibold text-amber-500/80 uppercase tracking-wider">
                  <button
                    onClick={() => setAnnouncementsOpen(!announcementsOpen)}
                    className="flex items-center gap-1 hover:text-amber-400 transition-colors"
                  >
                    {announcementsOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                    <Megaphone className="w-3 h-3" />
                    Announcements <span className="text-[8px] font-normal normal-case tracking-normal text-amber-500/50 ml-0.5">· Agency-Wide</span>
                  </button>
                </div>
                {announcementsOpen && (
                  <div role="list" className="mt-1 space-y-0.5">
                    {announcementChannels.map(ch => {
                      const isActive = ch.channel.id === activeChannelId;
                      const canPost = canPostInAnnouncement(ch);
                      // Show parent agency name if channel is from a different agency
                      const channelAgency = agencyList?.find(a => a.id === ch.channel.agencyId);
                      const isFromDifferentAgency = ch.channel.agencyId && ch.channel.agencyId !== currentAgencyId;

                      return (
                        <button
                          key={ch.channel.id}
                          onClick={() => onSelect(ch.channel.id)}
                          aria-current={isActive ? "page" : undefined}
                          className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-left transition-all duration-150 border ${
                            isActive
                              ? "bg-amber-500/15 text-amber-100 border-amber-500/30"
                              : ch.unreadCount > 0
                                ? "text-amber-200 font-semibold hover:bg-amber-500/10 border-amber-500/10"
                                : "text-zinc-400 hover:text-amber-300 hover:bg-amber-500/5 border-transparent"
                          }`}
                        >
                          <Megaphone className={`w-4 h-4 flex-shrink-0 ${isActive ? 'text-amber-400' : 'text-amber-500/70'}`} />
                          <div className="flex-1 min-w-0">
                            <span className="block truncate text-sm font-medium">{ch.channel.name}</span>
                            <span className="block text-[10px] text-amber-500/40 truncate leading-tight">Agency-Wide</span>
                          </div>
                          {ch.unreadCount > 0 && (
                            <span className="flex-shrink-0 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-amber-500 text-[10px] font-bold text-white px-1.5">
                              {ch.unreadCount > 99 ? "99+" : ch.unreadCount}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Channels section */}
            <div className="mb-3 overflow-hidden">
              <div className="flex items-center justify-between px-2 py-1 text-xs font-semibold text-zinc-500 uppercase tracking-wider">
                <button
                  onClick={() => setChannelsOpen(!channelsOpen)}
                  className="flex items-center gap-1 hover:text-zinc-300 transition-colors"
                >
                  {channelsOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                  Channels
                </button>
                <button
                  onClick={() => setIsCreating(!isCreating)}
                  className="p-0.5 rounded hover:bg-zinc-800 hover:text-zinc-300 transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </div>
              {isCreating && (
                <div className="px-2 mt-1 mb-2 overflow-hidden">
                  <input
                    value={newName}
                    onChange={e => setNewName(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === "Enter" && newName.trim()) {
                        onCreateChannel(newName.trim());
                        setNewName("");
                        setIsCreating(false);
                      }
                      if (e.key === "Escape") { setIsCreating(false); setNewName(""); }
                    }}
                    placeholder="channel-name"
                    className="w-full bg-zinc-800 rounded-md px-2.5 py-1.5 text-sm text-zinc-300 placeholder:text-zinc-600 border-none outline-none focus:ring-1 focus:ring-blue-500/50"
                    autoFocus
                  />
                  <p className="text-[10px] text-zinc-600 px-1 mt-1">Enter to create, Esc to cancel</p>
                </div>
              )}
              {channelsOpen && (
                <div role="list" className="mt-1 space-y-0.5">
                  {groupChannels.map(renderItem)}
                </div>
              )}
            </div>

            {/* DMs section — show existing DMs + all team members for quick DM */}
            <div>
              <div className="flex items-center justify-between">
                <button
                  onClick={() => setDmsOpen(!dmsOpen)}
                  className="flex items-center gap-1 px-2 py-1 text-xs font-semibold text-zinc-500 uppercase tracking-wider hover:text-zinc-300 transition-colors"
                >
                  {dmsOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                  Direct Messages
                </button>
                {onCreateGroupDm && (
                  <button
                    onClick={() => { setIsGroupDmOpen(true); setGroupDmSelected(new Set()); setGroupDmSearch(""); setGroupDmName(""); }}
                    className="p-1 mr-2 rounded hover:bg-white/10 text-zinc-500 hover:text-zinc-300 transition-colors"
                    title="New Group Chat"
                  >
                    <Users className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              {dmsOpen && (
                <div role="list" className="mt-1 space-y-0.5">
                  {/* Existing DM conversations */}
                  {dmChannels.map(ch => renderItem({ ...ch, channel: { ...ch.channel, name: getDmDisplayName(ch) } }))}

                  {/* Current agency members without existing DMs */}
                  {allMembers
                    .filter(m => m.userId !== currentUserId)
                    .filter(m => !dmChannels.some(dm => {
                      const shortId = m.userId.split('-')[0];
                      return dm.channel.slug.includes(shortId);
                    }))
                    .map(m => (
                      <button
                        key={`dm-${m.userId}`}
                        onClick={(e) => { e.stopPropagation(); onStartDm(m.userId, m.user.name || m.user.email); }}
                        className="w-full flex items-center gap-2.5 px-3 py-1.5 rounded-md text-left text-zinc-400 hover:text-zinc-200 hover:bg-white/5 transition-all"
                      >
                        <div className="relative">
                          <Avatar className="w-5 h-5">
                            <AvatarImage src={m.user.profileImageUrl} />
                            <AvatarFallback className="text-[8px] bg-zinc-700 font-semibold">
                              {getInitials(m.user.name || m.user.email)}
                            </AvatarFallback>
                          </Avatar>
                          <span aria-label={statusLabel(m.user.userStatus, m.user.isOnline)} className={`absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full border border-zinc-900 ${statusColor(m.user.userStatus, m.user.isOnline)}`} />
                        </div>
                        <span className="flex-1 truncate text-sm font-medium">{m.user.name || m.user.email}</span>
                      </button>
                    ))
                  }

                  {/* Agency-Wide — nested inside DMs (owner only) */}
                  {networkMembers && networkMembers.filter(a => a.depth > 0).length > 0 && (
                    <NetworkMembersSection
                      networkMembers={networkMembers.filter(a => a.depth > 0)}
                      currentUserId={currentUserId}
                      dmChannels={dmChannels}
                      onStartDm={onStartDm}
                    />
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </ScrollArea>

      {/* Group DM Modal */}
      <Dialog open={isGroupDmOpen} onOpenChange={setIsGroupDmOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-700 text-zinc-100 sm:max-w-md">
          <DialogHeader>
            <DialogTitle>New Group Chat</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Name your group and select 2 or more people to start.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {/* Group name */}
            <div>
              <input
                value={groupDmName}
                onChange={e => setGroupDmName(e.target.value)}
                placeholder="Group name"
                maxLength={50}
                className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-sm text-zinc-200 placeholder:text-zinc-500 outline-none focus:border-blue-500 transition-colors"
                autoFocus
              />
            </div>

            {/* Search */}
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700">
              <Search className="w-4 h-4 text-zinc-500 shrink-0" />
              <input
                value={groupDmSearch}
                onChange={e => setGroupDmSearch(e.target.value)}
                placeholder="Search members..."
                className="flex-1 bg-transparent text-sm text-zinc-200 placeholder:text-zinc-500 outline-none"
              />
            </div>

            {/* Selected pills */}
            {groupDmSelected.size > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {[...groupDmSelected].map(uid => {
                  const member = allMembers.find(m => m.userId === uid);
                  return (
                    <span
                      key={uid}
                      className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-600/20 text-blue-400 text-xs"
                    >
                      {member?.user.name || 'User'}
                      <button
                        onClick={() => {
                          const next = new Set(groupDmSelected);
                          next.delete(uid);
                          setGroupDmSelected(next);
                        }}
                        className="hover:text-blue-200"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  );
                })}
              </div>
            )}

            {/* Member list */}
            <ScrollArea className="max-h-[250px]">
              <div className="space-y-0.5">
                {filteredGroupMembers.map(m => {
                  const isSelected = groupDmSelected.has(m.userId);
                  return (
                    <button
                      key={m.userId}
                      onClick={() => {
                        const next = new Set(groupDmSelected);
                        if (isSelected) next.delete(m.userId);
                        else if (next.size < 10) next.add(m.userId);
                        setGroupDmSelected(next);
                      }}
                      className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-left transition-all ${
                        isSelected ? 'bg-blue-600/10 text-blue-300' : 'text-zinc-400 hover:text-zinc-200 hover:bg-white/5'
                      }`}
                    >
                      <div className="relative">
                        <Avatar className="w-6 h-6">
                          <AvatarImage src={m.user.profileImageUrl} />
                          <AvatarFallback className="text-[9px] bg-zinc-700 font-semibold">
                            {getInitials(m.user.name || m.user.email)}
                          </AvatarFallback>
                        </Avatar>
                        <span className={`absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full border border-zinc-900 ${statusColor(m.user.userStatus, m.user.isOnline)}`} />
                      </div>
                      <span className="flex-1 truncate text-sm font-medium">{m.user.name || m.user.email}</span>
                      {isSelected && <Check className="w-4 h-4 text-blue-400 shrink-0" />}
                    </button>
                  );
                })}
                {filteredGroupMembers.length === 0 && (
                  <p className="text-sm text-zinc-500 text-center py-4">No members found</p>
                )}
              </div>
            </ScrollArea>
          </div>

          <DialogFooter className="flex items-center justify-between">
            <Button variant="ghost" onClick={() => setIsGroupDmOpen(false)} className="text-zinc-400">
              Cancel
            </Button>
            <div className="flex items-center gap-2">
              <span className="text-xs text-zinc-500">{groupDmSelected.size}/10</span>
            <Button
              disabled={groupDmSelected.size < 2 || !groupDmName.trim()}
              onClick={() => {
                if (onCreateGroupDm && groupDmSelected.size >= 2) {
                  onCreateGroupDm([...groupDmSelected], groupDmName.trim() || undefined);
                  setIsGroupDmOpen(false);
                  setGroupDmSelected(new Set());
                  setGroupDmSearch("");
                  setGroupDmName("");
                }
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              Create Group ({groupDmSelected.size})
            </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Separate component for channel item — needs its own useState for hover
function ChannelItemRow({ ch, isActive, isDm, isArchived, isDefault, onSelect, onEditChannel, onDeleteChannel, onReorderChannel, onToggleAnnouncement, getDmDisplayName, setEditingChannelId, setEditName, onCloseDm }: any) {
  const isAnnouncement = ch.channel.isAnnouncement;
  const [showMenu, setShowMenu] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Only show for non-default, non-DM channels
  const canEdit = onEditChannel && !isDefault;
  const canDelete = onDeleteChannel && !isDefault;
  const canReorder = !!onReorderChannel;
  const canToggleAnnouncement = !!onToggleAnnouncement;
  const hasActions = canEdit || canDelete || canReorder || canToggleAnnouncement;

  return (
    <div className="relative"
      onMouseEnter={() => setShowMenu(true)}
      onMouseLeave={() => { if (!dropdownOpen) setShowMenu(false); }}
    >
      <button
        onClick={() => onSelect(ch.channel.id)}
        aria-current={isActive ? "page" : undefined}
        className={`w-full flex items-center gap-2.5 px-3 py-1.5 rounded-md text-left transition-all duration-150 ${
          isActive
            ? "bg-blue-600/20 text-white"
            : ch.unreadCount > 0
              ? "text-white font-semibold hover:bg-white/5"
              : isArchived
                ? "text-zinc-600 hover:text-zinc-400 hover:bg-white/5 opacity-60"
                : "text-zinc-500 hover:text-zinc-300 hover:bg-white/5"
        }`}
      >
        {isDm ? (
          <MessageSquare className="w-4 h-4 flex-shrink-0 opacity-60" />
        ) : isArchived ? (
          <Lock className="w-4 h-4 flex-shrink-0 opacity-60 text-amber-500/70" />
        ) : isAnnouncement ? (
          <Megaphone className="w-4 h-4 flex-shrink-0 text-amber-400" />
        ) : ch.channel.slug === 'sales-wins' ? (
          <Trophy className="w-4 h-4 flex-shrink-0 text-yellow-500 opacity-80" />
        ) : (
          <MessageSquare className="w-4 h-4 flex-shrink-0 opacity-60" />
        )}
        <span className={`flex-1 truncate text-sm font-medium ${isArchived ? 'italic' : ''}`}>
          {ch.channel.type === "dm" ? getDmDisplayName(ch) : ch.channel.name}
          {ch.agencyName && ch.channel.type !== "dm" && <span className="text-[9px] font-semibold text-red-400 ml-1 relative -top-[1px]">(Agency-Wide)</span>}
          {ch.agencyName && ch.channel.type === "dm" && <span className="text-[9px] font-normal text-zinc-500 ml-1 relative -top-[1px]">({ch.agencyName})</span>}
        </span>
        {ch.unreadCount > 0 && (
          <span className={`flex-shrink-0 min-w-[18px] h-[18px] flex items-center justify-center rounded-full text-[10px] font-bold text-white px-1.5 ${
            ch.agencyName
              ? 'bg-amber-500 shadow-[0_0_6px_rgba(245,158,11,0.5)]'
              : 'bg-emerald-500 shadow-[0_0_6px_rgba(16,185,129,0.5)]'
          }`}>
            {ch.unreadCount > 99 ? "99+" : ch.unreadCount}
          </span>
        )}
      </button>
      {/* DM close button */}
      {showMenu && isDm && onCloseDm && (
        <button
          className="absolute right-1 top-1/2 -translate-y-1/2 z-10 p-1 rounded bg-zinc-700/80 hover:bg-red-600/80 transition-colors"
          onClick={(e) => { e.stopPropagation(); onCloseDm(ch.channel.id); }}
          title="Close conversation"
        >
          <X className="w-3 h-3 text-zinc-300" />
        </button>
      )}
      {(showMenu || dropdownOpen) && !isDm && hasActions && (
        <div className="absolute right-1 top-1/2 -translate-y-1/2 z-10">
          <DropdownMenu open={dropdownOpen} onOpenChange={(open) => { setDropdownOpen(open); if (!open) setShowMenu(false); }}>
            <DropdownMenuTrigger asChild>
              <button className="p-1 rounded bg-zinc-700/80 hover:bg-zinc-600 transition-colors" onClick={e => e.stopPropagation()}>
                <MoreHorizontal className="w-3 h-3 text-zinc-300" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40" sideOffset={4}>
              {onReorderChannel && (
                <DropdownMenuItem onClick={() => { onReorderChannel(ch.channel.id, 'up'); setDropdownOpen(false); }}>
                  <ArrowUp className="w-3.5 h-3.5 mr-2" />
                  Move Up
                </DropdownMenuItem>
              )}
              {onReorderChannel && (
                <DropdownMenuItem onClick={() => { onReorderChannel(ch.channel.id, 'down'); setDropdownOpen(false); }}>
                  <ArrowDown className="w-3.5 h-3.5 mr-2" />
                  Move Down
                </DropdownMenuItem>
              )}
              {onToggleAnnouncement && (
                <DropdownMenuItem onClick={() => { onToggleAnnouncement(ch.channel.id); setDropdownOpen(false); }}>
                  <Megaphone className={`w-3.5 h-3.5 mr-2 ${isAnnouncement ? 'text-amber-500' : ''}`} />
                  {isAnnouncement ? 'Disable Announcements' : 'Announcement Mode'}
                </DropdownMenuItem>
              )}
              {canEdit && (
                <DropdownMenuItem onClick={() => { setEditingChannelId(ch.channel.id); setEditName(ch.channel.name); setDropdownOpen(false); }}>
                  <Pencil className="w-3.5 h-3.5 mr-2" />
                  Rename
                </DropdownMenuItem>
              )}
              {canDelete && (
                <DropdownMenuItem
                  className="text-red-600 focus:text-red-600"
                  onClick={() => {
                    setDropdownOpen(false);
                    setConfirmDelete(true);
                  }}
                >
                  <Trash2 className="w-3.5 h-3.5 mr-2" />
                  Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {/* Delete confirmation dialog */}
      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Delete Channel
            </DialogTitle>
            <DialogDescription className="text-zinc-500">
              Are you sure you want to delete <strong>#{ch.channel.name}</strong>? All messages in this channel will be lost. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setConfirmDelete(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                setConfirmDelete(false);
                onDeleteChannel(ch.channel.id);
              }}
            >
              Delete Channel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Network Members Section (Owner only — sub-agency members for DMs) ────────

type NetworkAgency = {
  agencyId: string; agencyName: string; parentAgencyId: string | null; depth: number;
  members: Array<{ userId: string; name: string; email: string; profileImageUrl?: string; isOnline?: boolean; userStatus?: string; agencyRole?: string }>;
};

function NetworkMembersSection({ networkMembers, currentUserId, dmChannels, onStartDm }: {
  networkMembers: NetworkAgency[];
  currentUserId?: string;
  dmChannels: Channel[];
  onStartDm: (userId: string, userName: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [expandedAgencies, setExpandedAgencies] = useState<Set<string>>(new Set());

  const toggleAgency = (id: string) => {
    setExpandedAgencies(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Build tree: group by parentAgencyId
  const rootAgencies = networkMembers.filter(a => a.depth === 1);
  const getChildren = (parentId: string) => networkMembers.filter(a => a.parentAgencyId === parentId);

  const totalMembers = networkMembers.reduce((s, a) => s + a.members.length, 0);
  const totalOnline = networkMembers.reduce((s, a) => s + a.members.filter(m => m.isOnline).length, 0);

  const renderAgencyGroup = (agency: NetworkAgency, indent: number) => {
    const isExpanded = expandedAgencies.has(agency.agencyId);
    const children = getChildren(agency.agencyId);
    const filteredMembers = agency.members
      .filter(m => m.userId !== currentUserId)
      .filter(m => !dmChannels.some(dm => dm.channel.slug.includes(m.userId.split('-')[0])));
    const onlineCount = agency.members.filter(m => m.isOnline).length;

    return (
      <div key={agency.agencyId}>
        <button
          onClick={() => toggleAgency(agency.agencyId)}
          className="w-full flex items-center gap-1.5 px-2 py-1.5 hover:bg-white/5 rounded-md transition-colors"
          style={{ paddingLeft: `${8 + indent * 12}px` }}
        >
          {isExpanded ? <ChevronDown className="w-3 h-3 text-zinc-500 flex-shrink-0" /> : <ChevronRight className="w-3 h-3 text-zinc-500 flex-shrink-0" />}
          <Building2 className="w-3 h-3 text-blue-400/70 flex-shrink-0" />
          <span className="text-[11px] font-semibold text-zinc-400 truncate flex-1 text-left">{agency.agencyName}</span>
          <span className="text-[9px] text-zinc-600 flex-shrink-0">{agency.members.length}</span>
          {onlineCount > 0 && <span className="w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0" />}
        </button>

        {isExpanded && (
          <div className="space-y-0.5">
            {filteredMembers.map(m => (
              <button
                key={`net-${m.userId}`}
                onClick={(e) => { e.stopPropagation(); onStartDm(m.userId, m.name || m.email); }}
                className="w-full flex items-center gap-2.5 py-1.5 rounded-md text-left text-zinc-400 hover:text-zinc-200 hover:bg-white/5 transition-all"
                style={{ paddingLeft: `${20 + indent * 12}px` }}
              >
                <div className="relative">
                  <Avatar className="w-5 h-5">
                    <AvatarImage src={m.profileImageUrl} />
                    <AvatarFallback className="text-[8px] bg-zinc-700 font-semibold">
                      {getInitials(m.name || m.email)}
                    </AvatarFallback>
                  </Avatar>
                  <span className={`absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full border border-zinc-900 ${m.isOnline ? 'bg-green-500' : 'bg-zinc-600'}`} />
                </div>
                <span className="flex-1 truncate text-sm font-medium">{m.name || m.email}</span>
                {m.agencyRole === 'owner' && (
                  <span className="text-[7px] font-bold px-1 py-0.5 rounded bg-amber-500/20 text-amber-400 mr-1">OWN</span>
                )}
                {m.agencyRole === 'admin' && (
                  <span className="text-[7px] font-bold px-1 py-0.5 rounded bg-blue-500/20 text-blue-400 mr-1">ADM</span>
                )}
              </button>
            ))}
            {/* Render sub-agencies recursively */}
            {children.map(child => renderAgencyGroup(child, indent + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="mb-3">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between w-full px-2 py-1 text-xs font-semibold text-blue-400/80 uppercase tracking-wider hover:text-blue-300 transition-colors"
      >
        <div className="flex items-center gap-1">
          {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          <Building2 className="w-3 h-3" />
          Agency-Wide
        </div>
        <div className="flex items-center gap-1.5 text-[9px] font-normal normal-case tracking-normal">
          <span className="text-zinc-500">{totalMembers}</span>
          {totalOnline > 0 && (
            <span className="flex items-center gap-0.5 text-green-500">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500" />{totalOnline}
            </span>
          )}
        </div>
      </button>

      {open && (
        <div className="mt-1 space-y-0.5">
          {rootAgencies.map(agency => renderAgencyGroup(agency, 0))}
        </div>
      )}
    </div>
  );
}

// ── Agency Org Chart Switcher ────────────────────────────────────────────────

type AgencyItem = { id: string; name: string; isCurrent: boolean; role?: string; parentAgencyId?: string; memberCount?: number; onlineCount?: number; logoUrl?: string };

function AgencyTreeSwitcher({
  agencyList,
  chatAgencyId,
  agencyName,
  onSwitchAgency,
}: {
  agencyList: AgencyItem[];
  chatAgencyId?: string | null;
  agencyName?: string;
  onSwitchAgency: (agencyId: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const panStart = useRef({ x: 0, y: 0 });
  const selectedName = agencyList.find(a => a.id === chatAgencyId)?.name || agencyName || "Team Chat";

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button:not([data-pan])')) return; // don't drag when clicking cards
    setDragging(true);
    dragStart.current = { x: e.clientX, y: e.clientY };
    panStart.current = { ...pan };
  }, [pan]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!dragging) return;
    setPan({
      x: panStart.current.x + (e.clientX - dragStart.current.x),
      y: panStart.current.y + (e.clientY - dragStart.current.y),
    });
  }, [dragging]);

  const handleMouseUp = useCallback(() => setDragging(false), []);

  // Reset pan/zoom when opening
  useEffect(() => {
    if (open) { setZoom(1); setPan({ x: 0, y: 0 }); }
  }, [open]);

  const getChildren = useCallback((parentId: string) =>
    agencyList.filter(a => a.parentAgencyId === parentId),
  [agencyList]);

  const getDescendantCount = useCallback((id: string): number => {
    const ch = getChildren(id);
    return ch.reduce((s: number, c: AgencyItem) => s + 1 + getDescendantCount(c.id), 0);
  }, [getChildren]);

  const rootAgencies = useMemo(() =>
    agencyList.filter(a => !a.parentAgencyId || !agencyList.some(p => p.id === a.parentAgencyId)),
  [agencyList]);

  const totalOnline = agencyList.reduce((s, a) => s + (a.onlineCount || 0), 0);
  const totalMembers = agencyList.reduce((s, a) => s + (a.memberCount || 0), 0);

  const OrgNode = ({ agency, isRoot }: { agency: AgencyItem; isRoot?: boolean }) => {
    const isSelected = (agency.isCurrent && !chatAgencyId) || agency.id === chatAgencyId;
    const children = getChildren(agency.id);
    const online = agency.onlineCount || 0;
    const members = agency.memberCount || 0;

    return (
      <div className="flex flex-col items-center">
        <button
          onClick={() => { onSwitchAgency(agency.isCurrent ? null : agency.id); setOpen(false); }}
          className={`relative rounded-xl border-2 transition-all duration-200 text-center cursor-pointer w-[130px] hover:shadow-md hover:-translate-y-0.5 ${
            isSelected
              ? "border-blue-500 bg-blue-50 dark:bg-blue-950/30 shadow-md shadow-blue-500/15"
              : isRoot
                ? "border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 hover:border-blue-300"
                : "border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 hover:border-blue-300"
          }`}
        >
          {isSelected && (
            <div className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center z-10">
              <Check className="w-2.5 h-2.5 text-white" />
            </div>
          )}

          <div className="px-2.5 py-2">
            {agency.logoUrl ? (
              <img src={agency.logoUrl} alt="" className="w-8 h-8 rounded-lg mx-auto mb-1 object-cover" />
            ) : (
              <div className={`w-8 h-8 rounded-lg mx-auto mb-1 flex items-center justify-center text-sm font-bold ${
                isSelected ? "bg-blue-500 text-white" :
                isRoot ? "bg-zinc-800 text-white dark:bg-zinc-200 dark:text-zinc-800" :
                "bg-zinc-100 dark:bg-zinc-700 text-zinc-600 dark:text-zinc-300"
              }`}>
                {agency.name?.charAt(0) || "?"}
              </div>
            )}

            <p className={`text-[10px] font-bold truncate leading-tight ${isSelected ? "text-blue-600 dark:text-blue-300" : "text-zinc-800 dark:text-zinc-100"}`}>
              {agency.name}
            </p>

            {/* Stats */}
            <p className="text-[9px] text-zinc-400 mt-1">
              {members > 0 ? `${members} agents` : 'Agency'}
              {online > 0 && <span className="text-emerald-500"> · {online} online</span>}
            </p>
          </div>
        </button>

        {/* Children */}
        {children.length > 0 && (
          <div className="flex flex-col items-center">
            <div className="w-px h-5 bg-zinc-300 dark:bg-zinc-600" />
            {children.length === 1 ? (
              <OrgNode agency={children[0]} />
            ) : (
              <>
                {/* Horizontal bar */}
                <div className="flex">
                  {children.map((child: AgencyItem, i: number) => (
                    <div key={child.id} className="flex flex-col items-center" style={{ minWidth: '138px' }}>
                      {/* Top connector: horizontal line + vertical drop */}
                      <div className="w-full flex items-start">
                        <div className={`h-px bg-zinc-300 dark:bg-zinc-600 ${i === 0 ? 'w-1/2 ml-auto' : i === children.length - 1 ? 'w-1/2' : 'w-full'}`} />
                      </div>
                      <div className="w-px h-5 bg-zinc-300 dark:bg-zinc-600" />
                      <OrgNode agency={child} />
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <button onClick={() => setOpen(!open)} className="flex items-center gap-1.5 hover:bg-white/10 rounded-md px-1.5 py-0.5 -ml-1.5 transition-colors w-full text-left group">
        <h2 className="text-sm font-bold text-white truncate flex-1">{selectedName}</h2>
        <div className="flex items-center gap-0.5 flex-shrink-0 opacity-60 group-hover:opacity-100 transition-opacity">
          <Building2 className="w-3 h-3 text-zinc-400" />
          <ChevronDown className={`w-3 h-3 text-zinc-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        </div>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-[92vw] w-auto max-h-[85vh] p-0 overflow-hidden bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700" aria-describedby="org-chart-desc">
          <DialogDescription id="org-chart-desc" className="sr-only">Select an agency to view their team chat</DialogDescription>
          <DialogTitle className="px-5 pt-4 pb-3 border-b border-zinc-100 dark:border-zinc-800">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                  <Building2 className="w-4 h-4 text-white" />
                </div>
                <div>
                  <span className="text-sm font-bold text-zinc-800 dark:text-zinc-200 block">Agency-Wide</span>
                  <span className="text-[10px] text-zinc-400">Select an agency to view their chat</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {/* Stats */}
                <div className="flex items-center gap-3 text-[10px] text-zinc-400 mr-2">
                  <span className="flex items-center gap-1"><Building2 className="w-3 h-3" /> {agencyList.length}</span>
                  {totalMembers > 0 && <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {totalMembers}</span>}
                  {totalOnline > 0 && (
                    <span className="flex items-center gap-1 text-emerald-500">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> {totalOnline} online
                    </span>
                  )}
                </div>
                {/* Zoom controls */}
                <div className="flex items-center gap-1 bg-zinc-100 dark:bg-zinc-800 rounded-lg px-1 py-0.5">
                  <button onClick={() => setZoom(z => Math.max(0.4, z - 0.1))} className="w-6 h-6 flex items-center justify-center rounded hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-500 text-sm font-bold">−</button>
                  <button onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }} className="text-[10px] text-zinc-400 w-10 text-center font-medium hover:text-zinc-600 dark:hover:text-zinc-300">{Math.round(zoom * 100)}%</button>
                  <button onClick={() => setZoom(z => Math.min(2, z + 0.1))} className="w-6 h-6 flex items-center justify-center rounded hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-500 text-sm font-bold">+</button>
                </div>
              </div>
            </div>
          </DialogTitle>
          <div
            className="overflow-hidden p-8 flex justify-center min-h-[300px] select-none"
            style={{ cursor: dragging ? 'grabbing' : 'grab' }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            <div style={{
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
              transformOrigin: 'top center',
              transition: dragging ? 'none' : 'transform 150ms ease',
            }}>
              <div className="flex flex-col items-center">
                {rootAgencies.map((a: AgencyItem) => (
                  <OrgNode key={a.id} agency={a} isRoot />
                ))}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
